'use strict';


import { Box3, Matrix4, Plane, Quaternion, Vector3 } from 'three';
import gsap from "gsap";
import { Buildings, GSAPAnimationMixer, isMobile, projectMode, threeJsVariables } from '../../settings';
import { getUrlParam, isCylinderDisplayModeEqual, isOneMesh, multimaterialNeedsUpdate } from '../../functions';
import {updateShadowMap} from "./lights";
import { sceneObjectsGroup } from '../points/points';

const DEFAULT_PARAMS = {
    active: false,
    geometricCenter: false,
    distance: 500,
    offset: 2,
    introHeight: 20,
    heightOffset: 0
};

export class Slicer {
    constructor (renderer,params) {
        this.params = params || DEFAULT_PARAMS;

        if (this.active) {
            renderer.localClippingEnabled = true;
        }

        this.presets = {};
        this.selectedPreset = null;
        this.bbox = new Box3();
        this.size = new Vector3();
        this.center = new Vector3();
        this.numberOfPlanes = 5;
        this.planes = [];
        this.setDefaultPlanes();
    }

    setDefaultPlanes() {
        this.planes.length = 0;
        const x1 = new Plane( new Vector3( - 1, 0, 0 ), this.params.distance );
        x1.defaultNormal = new Vector3().copy(x1.normal);
        const x2 = new Plane( new Vector3(  - 1, 0, 0 ), -this.params.distance ).negate();
        x2.defaultNormal = new Vector3().copy(x2.normal);
        const y = new Plane( new Vector3( 0, - 1, 0 ), this.params.distance );
        y.defaultNormal = new Vector3().copy(y.normal);
        const z1 = new Plane( new Vector3( 0, 0, - 1 ), this.params.distance );
        z1.defaultNormal = new Vector3().copy(z1.normal);
        const z2 = new Plane( new Vector3( 0, 0, - 1 ), -this.params.distance ).negate();
        z2.defaultNormal = new Vector3().copy(z2.normal);

        this.planes.push(x1,x2,y,z1,z2);

        const defaultPlanesCount = 5;
        const count = this.numberOfPlanes - defaultPlanesCount;
        if (count > 0) {
            for (let i = 0; i < count; i++) {
                this.addNewPlane();
            }
        }
    }

    addNewPlane() {
        const plane = new Plane( new Vector3( - 1, 0, 0 ), this.params.distance );
        plane.defaultNormal = new Vector3().copy(plane.normal);
        this.planes.push(plane);
    }

    add(mesh) {
        if (!this.active) return;
        if (Array.isArray(mesh.material)) {
            mesh.material.forEach(subMaterial => {
                subMaterial.clippingPlanes = this.planes;
                subMaterial.clipShadows = true;

            });
        } else {
            mesh.material.clipShadows = true;
            mesh.material.clippingPlanes = this.planes;
        }
    }

    keyPlanEnter(building) {
        if (projectMode.LiteMode.isActive() && projectMode.LiteMode.modeIs('schematic'))
            return;
        const copy = building.modelGroup.clone(true);
        let clonedMaterial = [];
        let material = copy.children[0]?.material;

        if (Array.isArray(material)) {
            material.forEach(subMaterial => {
                const clone = subMaterial.clone();
                clone.clippingPlanes = null;
                clone.depthWrite = false;
                clone.transparent = true;
                clone.opacity = 0.7;
                clonedMaterial.push(clone);
            });

        } else {
            const clone = material.clone();
            clone.depthWrite = false;
            clone.clippingPlanes = null;
            clone.transparent = true;
            clone.opacity = 0.7;
            clonedMaterial = clone;
        }

        copy.traverse(el => {
            if (el.isMesh) {
                el.castShadow = false;
                el.receiveShadow = false;
                el.material = clonedMaterial;
            }
        });

        threeJsVariables.scene.add(copy);

        return gsap.to(clonedMaterial,{
            opacity: 0,
            duration: GSAPAnimationMixer.duration,
            ease: GSAPAnimationMixer.ease,
            onUpdate: multimaterialNeedsUpdate,
            onComplete: () => {
                copy.removeFromParent();
            }
        });
    }

    restoreXZ(duration) {
        if (config.features.privateHouseMode) return;
        const animations = [];
        this.planes.forEach(plane => {
            animations.push(Slicer.#getAnimationNormal(plane,duration));
            animations.push(Slicer.#getAnimationConstant(plane,duration,this.params.distance));
        });

        return animations;
    }

    intro() {
        if (!this.active) return;

        const buildings = Buildings.getBuildingsArray();

        if (this.y === this.distance) {
            buildings.forEach(building => building.showSceneObjectsOfCurrentBuilding());
            return;
        }

        buildings.forEach(building => building.hideSceneObjectsOfCurrentBuilding());

        const start = {
            value: this.params.introHeight
        };

        const box = new Box3();
        const size = new Vector3();
        let showObjects = false;
        buildings.forEach(building => box.expandByObject(building.modelGroup));
        box.getSize(size);

        return gsap.to(start,{
            value: this.params.distance,
            ease: GSAPAnimationMixer.ease,
            duration: GSAPAnimationMixer.duration * 5,
            onUpdate: () => {
                this.y = start.value;
                if (start.value > size.y && !showObjects) {
                    showObjects = true;
                    buildings.forEach(building => building.showSceneObjectsOfCurrentBuilding());
                }

                updateShadowMap();
            }
        });
    }

    setInitialIntroHeight() {
        if (!this.active) return;

        const inSearch = getUrlParam('p') === 'search';
        if (inSearch) {
            this.y = this.params.distance;
            return;
        }

        this.y = this.params.introHeight;
    }

    cropXZ(duration) {
        if (config.features.privateHouseMode || isCylinderDisplayModeEqual('show all')) return;
        const animations = [];

        if (this.selectedPreset) {
            this.planes.forEach((plane,index) => {
                const presetPlane = this.selectedPreset[index];
                const vec = presetPlane ? presetPlane.normal : null;
                const constant = presetPlane ? presetPlane.constant : this.params.distance;
                animations.push(Slicer.#getAnimationNormal(plane,duration,vec));
                animations.push(Slicer.#getAnimationConstant(plane,duration,constant));
            });
        } else {
            const offset = this.params.offset;
            const end = {
                0: (this.center.x + this.size.x / 2) + offset,
                1: (this.size.x / 2  - this.center.x) + offset,
                3: (this.center.z + this.size.z / 2) + offset,
                4: (this.size.z / 2 - this.center.z) + offset
            };

            this.planes.forEach((plane,index) => {
                const planeConstant = end[index] ? end[index] : this.params.distance;
                animations.push(Slicer.#getAnimationNormal(plane,duration));
                animations.push(Slicer.#getAnimationConstant(plane,duration,planeConstant));
            });
        }

        return animations;
    }

    setSlicerPresets(data) {
        this.presets = data;
    }

    set target(object) {
        this.bbox.setFromObject(object).getSize(this.size);
        if (this.params?.geometricCenter) {
            this.bbox.getCenter(this.center);
        } else {
            object.getWorldPosition(this.center);
        }

        this.selectedPreset = this.presets[object.name];
    }

    set y(y) {
        this.planes[2].constant = y + this.heightOffset;
    }

    set x1(x) {
        this.planes[0].constant = x;
    }

    set x2(x) {
        this.planes[1].constant = x;
    }

    set z1(z) {
        this.planes[3].constant = z;
    }

    set z2(z) {
        this.planes[4].constant = z;
    }

    get y() {
        return this.planes[2].constant;
    }

    get active() {
        return this.params.active;
    }

    get distance() {
        return this.params.distance;
    }

    get heightOffset() {
        return this.params.heightOffset || DEFAULT_PARAMS.heightOffset;
    }

    static #getAnimationNormal(plane, duration, vec) {
        const vector = vec ? vec : plane.defaultNormal;
        
        if (duration === 'immediately') {
            plane.normal.copy(vector);
            return true;
        }
        
        return gsap.to(plane.normal,{
            x: vector.x,
            y: vector.y,
            z: vector.z,
            ease: GSAPAnimationMixer.ease,
            duration: duration || GSAPAnimationMixer.duration
        });
    }

    static #getAnimationConstant(plane,duration,constant) {
        const start = {value: plane.constant};
        
        if (duration === 'immediately') {
            plane.constant = constant;
            return true;
        }
        
        return gsap.to(start,{
            value: constant,
            ease: GSAPAnimationMixer.ease,
            duration: duration || GSAPAnimationMixer.duration,
            onUpdate: () => plane.constant = start.value
        });
    }

}
